// 🔧 category_builder.js
// ✅ Purpose: Category builder for the LGV Demo Engine
// ✅ Features:
// - Creates industry-specific demo categories in Firestore
// - Uses the event created by Event Builder
// - Stores categories under /events/{eventId}/categories
// - Keeps category creation separate from event, entries, users, and votes
// - Returns created category metadata for downstream Entry Builder migration
// - Fully block-commented for clarity and future devs

import {
  collection,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { logger } from "../../utils/logger";
import { generateCategoriesForIndustry } from "../generators/category_generator";
import { commitBatchedWrites } from "../repositories/batched_writes";

export const buildCategories = async ({
  eventId,
  demoConfig,
  industryTemplate,
} = {}) => {
  const plannedCategories = generateCategoriesForIndustry({
    industryKey:
      industryTemplate?.key ||
      demoConfig?.industry ||
      "car_show",
  });

  if (!eventId) {
    throw new Error("Category Builder requires an eventId.");
  }

  logger.log("🧱 Category Builder creating Firestore categories:", {
    value: {
      eventId,
      demoSize: demoConfig?.size || "small",
      plannedCategoryCount: plannedCategories.length,
      plannedCategories,
    },
  });

  const createdCategories = [];

  const operations = [];

  for (let index = 0; index < plannedCategories.length; index += 1) {
    const category = plannedCategories[index];
    const categoryName = category.name;

    const categoryDoc = {
      name: categoryName,
      title: categoryName,
      demoSeed: true,
      displayOrder: index + 1,
      description: category.description || "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...(category.isDefault ? { id: 1, isDefault: true, sortOrder: index + 1, eventId } : {}),
    };

    const categoryRef = category.isDefault
      ? doc(db, "events", eventId, "categories", "1")
      : doc(collection(db, "events", eventId, "categories"));

    operations.push((batch) => batch.set(categoryRef, categoryDoc));

    createdCategories.push({
      id: category.isDefault ? 1 : categoryRef.id,
      ref: categoryRef,
      name: categoryName,
      displayOrder: index + 1,
      ...(category.isDefault ? { isDefault: true, firestoreId: categoryRef.id } : {}),
    });
  }

  await commitBatchedWrites({ operations });

  logger.log("✅ Category Builder created Firestore categories:", {
    value: {
      eventId,
      createdCategoryCount: createdCategories.length,
    },
  });

  return {
    builder: "category_builder",
    ready: true,
    createdCount: createdCategories.length,
    createdCategories,
    message: "Category Builder created demo categories.",
  };
};

export const buildCategoryShell = async ({
  eventResult,
  demoConfig,
  industryTemplate,
} = {}) => {
  if (!eventResult?.eventId) {
    const plannedCategories = generateCategoriesForIndustry({
      industryKey:
        industryTemplate?.key ||
        demoConfig?.industry ||
        "car_show",
    });

    logger.warn("⚠️ Category Builder shell used without Event Builder result.", {
      value: {
        plannedCategoryCount: plannedCategories.length,
      },
    });

    return {
      builder: "category_builder",
      ready: false,
      plannedCount: plannedCategories.length,
      plannedCategories,
      message: "Category Builder needs an eventId before Firestore creation.",
    };
  }

  return buildCategories({
    eventId: eventResult.eventId,
    demoConfig,
    industryTemplate,
  });
};
